import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useState } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { LoadingSkeleton } from "./loading-skeleton.js";
const TabbedContainer = ({
  tabs,
  defaultTab,
  urlParam = "tab",
  mode = "tabs",
  orientation = "horizontal",
  className = "",
  tabsClassName = "",
  contentClassName = "",
  onTabChange,
  loading = false,
  allowMultipleOpen = false,
  collapsible = true
}) => {
  const [activeTabs, setActiveTabs] = useState(() => {
    let initialTab;
    if (typeof window === "undefined") {
      initialTab = defaultTab || tabs[0]?.id;
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const urlTabId = urlParams.get(urlParam);
      if (urlTabId && tabs.some((tab) => tab.id === urlTabId)) {
        initialTab = urlTabId;
      } else {
        initialTab = defaultTab || tabs[0]?.id;
      }
    }
    return new Set(initialTab ? [initialTab] : []);
  });
  const handleTabClick = (tabId) => {
    if (tabs.find((tab) => tab.id === tabId)?.disabled) {
      return;
    }
    setActiveTabs((prev) => {
      const newActiveTabs = new Set(prev);
      if (mode === "tabs") {
        newActiveTabs.clear();
        newActiveTabs.add(tabId);
      } else {
        if (newActiveTabs.has(tabId)) {
          if (collapsible) {
            newActiveTabs.delete(tabId);
          }
        } else {
          if (!allowMultipleOpen) {
            newActiveTabs.clear();
          }
          newActiveTabs.add(tabId);
        }
      }
      return newActiveTabs;
    });
    if (typeof window !== "undefined" && urlParam) {
      const url = new URL(window.location.href);
      url.searchParams.set(urlParam, tabId);
      window.history.replaceState({}, "", url.toString());
    }
    onTabChange?.(tabId);
  };
  const isVertical = orientation === "vertical" || orientation === "vertical-left" || orientation === "vertical-right";
  const isVerticalRight = orientation === "vertical-right";
  const renderTabsMode = () => {
    const activeTabId = Array.from(activeTabs)[0];
    const activeTab = tabs.find((tab) => tab.id === activeTabId);
    if (isVertical) {
      return /* @__PURE__ */ jsxs("div", { className: cn(
        "flex flex-col lg:gap-6",
        isVerticalRight ? "lg:flex-row-reverse" : "lg:flex-row",
        "bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700",
        className
      ), children: [
        /* @__PURE__ */ jsx("div", { className: cn(
          "flex-shrink-0",
          "lg:w-48 xl:w-56",
          "border-gray-200 dark:border-slate-700",
          "border-b lg:border-b-0",
          isVerticalRight ? "lg:border-l" : "lg:border-r",
          "bg-gray-50 dark:bg-slate-800",
          tabsClassName
        ), children: /* @__PURE__ */ jsx("nav", { className: "flex lg:flex-col p-2 lg:space-y-1 gap-1 lg:gap-0 overflow-x-auto lg:overflow-x-visible", role: "tablist", children: tabs.map((tab) => {
          const isActive = activeTabs.has(tab.id);
          const Icon = tab.icon;
          return /* @__PURE__ */ jsxs(
            "button",
            {
              role: "tab",
              "aria-selected": isActive,
              disabled: tab.disabled,
              onClick: () => handleTabClick(tab.id),
              className: cn(
                "group relative flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2.5 lg:py-3 text-sm font-medium rounded-lg whitespace-nowrap lg:whitespace-normal",
                "transition-all duration-200 ease-out",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                !isActive && [
                  "text-gray-600 dark:text-gray-400",
                  "hover:text-gray-900 dark:hover:text-gray-100",
                  "hover:bg-white dark:hover:bg-slate-700"
                ],
                isActive && [
                  "text-blue-600 dark:text-blue-400",
                  "bg-white dark:bg-slate-700",
                  "shadow-sm"
                ],
                tab.disabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
              ),
              children: [
                /* @__PURE__ */ jsx("div", { className: cn(
                  "hidden lg:block absolute top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full transition-all duration-200",
                  isVerticalRight ? "right-0" : "left-0",
                  isActive ? "bg-blue-500 dark:bg-blue-400" : "bg-transparent"
                ) }),
                Icon && /* @__PURE__ */ jsx(Icon, { className: cn(
                  "w-4 h-4 flex-shrink-0 transition-colors",
                  isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
                ) }),
                /* @__PURE__ */ jsx("span", { className: "truncate text-left", children: tab.label })
              ]
            },
            tab.id
          );
        }) }) }),
        /* @__PURE__ */ jsx("div", { className: cn("flex-1 min-w-0 p-4 lg:p-6", contentClassName), children: loading ? /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
          /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-4 w-3/4" }),
          /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-4 w-1/2" }),
          /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-32 w-full" })
        ] }) : activeTab?.component ? React__default.createElement(activeTab.component, activeTab.props || {}) : /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-16 text-gray-500 dark:text-gray-400", children: /* @__PURE__ */ jsx("div", { className: "text-center text-sm", children: "No content available" }) }) })
      ] });
    }
    return /* @__PURE__ */ jsxs("div", { className: cn(
      "bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700",
      className
    ), children: [
      /* @__PURE__ */ jsx("div", { className: cn(
        "border-b border-gray-200 dark:border-slate-700",
        "bg-gray-50 dark:bg-slate-800 px-2 py-2",
        tabsClassName
      ), children: /* @__PURE__ */ jsx("nav", { className: "flex gap-1 overflow-x-auto scrollbar-hide", role: "tablist", children: tabs.map((tab) => {
        const isActive = activeTabs.has(tab.id);
        const Icon = tab.icon;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            role: "tab",
            "aria-selected": isActive,
            disabled: tab.disabled,
            onClick: () => handleTabClick(tab.id),
            className: cn(
              "relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg whitespace-nowrap",
              "transition-all duration-200 ease-out",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
              !isActive && [
                "text-gray-600 dark:text-gray-400",
                "hover:text-gray-900 dark:hover:text-gray-100",
                "hover:bg-white dark:hover:bg-slate-700"
              ],
              isActive && [
                "text-blue-600 dark:text-blue-400",
                "bg-white dark:bg-slate-700",
                "shadow-sm"
              ],
              tab.disabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
            ),
            children: [
              /* @__PURE__ */ jsx("div", { className: cn(
                "absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 rounded-full transition-all duration-200",
                isActive ? "bg-blue-500 dark:bg-blue-400" : "bg-transparent"
              ) }),
              Icon && /* @__PURE__ */ jsx(Icon, { className: cn(
                "w-4 h-4 flex-shrink-0",
                isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
              ) }),
              /* @__PURE__ */ jsx("span", { className: "truncate", children: tab.label })
            ]
          },
          tab.id
        );
      }) }) }),
      /* @__PURE__ */ jsx("div", { className: cn("p-4", contentClassName), children: loading ? /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
        /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-4 w-3/4" }),
        /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-4 w-1/2" }),
        /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-32 w-full" })
      ] }) : activeTab?.component ? React__default.createElement(activeTab.component, activeTab.props || {}) : /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-16 text-gray-500 dark:text-gray-400", children: /* @__PURE__ */ jsx("div", { className: "text-center text-sm", children: "No content available" }) }) })
    ] });
  };
  const renderAccordionMode = () => {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: tabs.map((tab) => {
      const isActive = activeTabs.has(tab.id);
      const Icon = tab.icon;
      return /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn(
            "bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700",
            "overflow-hidden transition-all duration-200",
            isActive && "shadow-sm"
          ),
          children: [
            /* @__PURE__ */ jsxs(
              "button",
              {
                onClick: () => handleTabClick(tab.id),
                disabled: tab.disabled,
                className: cn(
                  "w-full flex items-center justify-between px-4 py-3 text-left",
                  "transition-colors duration-200",
                  "hover:bg-gray-50 dark:hover:bg-slate-800",
                  "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset",
                  tab.disabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
                ),
                children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                    Icon && /* @__PURE__ */ jsx(Icon, { className: cn(
                      "w-4 h-4 flex-shrink-0",
                      isActive ? "text-blue-600 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"
                    ) }),
                    /* @__PURE__ */ jsx("span", { className: cn(
                      "font-medium text-sm",
                      isActive ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
                    ), children: tab.label })
                  ] }),
                  /* @__PURE__ */ jsx(ChevronRight, { className: cn(
                    "w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-200",
                    isActive && "rotate-90"
                  ) })
                ]
              }
            ),
            isActive && /* @__PURE__ */ jsx("div", { className: cn(
              "border-t border-gray-200 dark:border-slate-700 px-4 py-4",
              contentClassName
            ), children: loading ? /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
              /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-3 w-3/4" }),
              /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-3 w-1/2" }),
              /* @__PURE__ */ jsx(LoadingSkeleton, { className: "h-24 w-full" })
            ] }) : tab.component ? React__default.createElement(tab.component, tab.props || {}) : /* @__PURE__ */ jsx("div", { className: "text-center py-8 text-gray-500 dark:text-gray-400 text-sm", children: "No content available" }) })
          ]
        },
        tab.id
      );
    }) });
  };
  if (tabs.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "text-center py-12 text-gray-500 dark:text-gray-400", children: /* @__PURE__ */ jsx("div", { className: "text-sm", children: "No tabs configured" }) });
  }
  return mode === "accordion" ? renderAccordionMode() : renderTabsMode();
};
export {
  TabbedContainer,
  TabbedContainer as default
};
//# sourceMappingURL=tabbed-container.js.map
